import { Injectable } from '@angular/core'
import {
  EventType,
  GlobalMessagingService,
  HeliosMessage,
  SessionMessagingService,
} from '@helios/messaging'
import {
  RootState,
  selectActiveSessionIdentifier,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { Subject } from 'rxjs'
import { distinctUntilChanged } from 'rxjs/operators'

const SOCKET_URL = `helios-ui-events/socket`
const GLOBAL_SOCKET_URL = `${SOCKET_URL}/global/`
const SESSION_SOCKET_URL = `${SOCKET_URL}/session/{sessionId}/`
const SESSION_ID_FILTER_KEY = 'heliosSessionId'
const ACCOUNT_FILTER_KEY = 'globalAccountFilter'
const ACCOUNT_JOBS_FILTER_KEY = 'globalAccountJobsFilter'

@Injectable({
  providedIn: 'root',
})
export class HeliosMessagingService {
  isSessionConnected$ = new Subject<boolean>()
  isGlobalConnected$ = new Subject<boolean>()

  constructor(
    private readonly globalMessaging: GlobalMessagingService,
    private readonly sessionMessaging: SessionMessagingService,
    private readonly store: Store<RootState>,
  ) {}

  init() {
    this.establishConnections()
    this.setConnectionStatus()
  }

  /**
   * This method sets up the connection status observables.
   * These are used for the footer indicators
   */
  setConnectionStatus() {
    this.globalMessaging
      .on<boolean, HeliosMessage<boolean>>(EventType.CONNECTED)
      .subscribe(e => {
        this.isGlobalConnected$.next(e.payload)
      })

    this.sessionMessaging
      .on<boolean, HeliosMessage<boolean>>(EventType.CONNECTED)
      .subscribe(e => {
        this.isSessionConnected$.next(e.payload)
      })
  }

  /**
   * Establish global and session connections.
   */
  establishConnections() {
    this.globalMessaging.connect(GLOBAL_SOCKET_URL)

    this.globalMessaging.setFilters(ACCOUNT_FILTER_KEY, [
      { jsonPath: '/payload/accountId', values: [] },
    ])
    this.globalMessaging.setFilters(ACCOUNT_JOBS_FILTER_KEY, [
      { jsonPath: '/payload/affectedAccountIds', values: [] },
    ])

    this.store
      .select(selectActiveSessionIdentifier)
      .pipe(distinctUntilChanged())
      .subscribe(sessionId => this.handleSessionChange(sessionId))
  }

  /**
   * Performs the session change logic. Disconnect and reconnect to the new session.
   */
  handleSessionChange(sessionId: string | undefined) {
    // Cleanup old session connection, regardless if a sessionId exists or not
    this.sessionMessaging.disconnect()

    this.globalMessaging.setFilters(SESSION_ID_FILTER_KEY, [
      { jsonPath: '/heliosSessionId', values: sessionId ? [sessionId] : [] },
    ])

    if (!sessionId) {
      return
    }

    // Connect to new session
    this.sessionMessaging.connect(
      SESSION_SOCKET_URL.replace('{sessionId}', sessionId),
    )
  }
}
